import { useEffect, useRef } from 'react';

type OutsideEvent = MouseEvent | TouchEvent;

type UseClickOutsideOptions = {
  enabled?: boolean;
  events?: Array<'mousedown' | 'touchstart'>; // Which events to listen to. Defaults to ['mousedown', 'touchstart'].
};

export function useClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onOutside: (event: OutsideEvent) => void,
  options: UseClickOutsideOptions = {},
) {
  const { enabled = true, events = ['mousedown', 'touchstart'] } = options;
  const onOutsideRef = useRef(onOutside);

  useEffect(() => {
    onOutsideRef.current = onOutside;
  }, [onOutside]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = (event: Event) => {
      const target = event.target as Node | null;
      const el = ref.current;
      if (!el || !target) {
        return;
      }

      if (!el.contains(target)) {
        onOutsideRef.current(event as OutsideEvent);
      }
    };

    events.forEach((eventName) => {
      document.addEventListener(eventName, handler);
    });

    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(eventName, handler);
      });
    };
  }, [enabled, events, ref]);
}
